import type { ReactNode } from "react";
import type { AdminStatusTone } from "@/lib/adminCodeDisplay";
import { adminStatusToneClass } from "@/lib/adminCodeDisplay";
import { cn } from "../common/utils";

const metricToneClass: Record<AdminStatusTone, { cardBg: string; border: string; surface: string }> = {
  success: { cardBg: "bg-emerald-100/70", border: "border-emerald-200", surface: "bg-white/75" },
  warning: { cardBg: "bg-orange-100/70", border: "border-orange-200", surface: "bg-white/75" },
  danger: { cardBg: "bg-red-100/70", border: "border-red-200", surface: "bg-white/75" },
  info: { cardBg: "bg-blue-100/70", border: "border-blue-200", surface: "bg-white/75" },
  neutral: { cardBg: "bg-slate-100/80", border: "border-slate-200", surface: "bg-white/75" },
  emerald: { cardBg: "bg-emerald-100/70", border: "border-emerald-200", surface: "bg-white/75" },
  blue: { cardBg: "bg-blue-100/70", border: "border-blue-200", surface: "bg-white/75" },
  amber: { cardBg: "bg-amber-100/70", border: "border-amber-200", surface: "bg-white/75" },
  orange: { cardBg: "bg-orange-100/70", border: "border-orange-200", surface: "bg-white/75" },
  red: { cardBg: "bg-red-100/70", border: "border-red-200", surface: "bg-white/75" },
  rose: { cardBg: "bg-rose-100/70", border: "border-rose-200", surface: "bg-white/75" },
  slate: { cardBg: "bg-slate-100/80", border: "border-slate-200", surface: "bg-white/75" },
  violet: { cardBg: "bg-violet-100/70", border: "border-violet-200", surface: "bg-white/75" },
  teal: { cardBg: "bg-teal-100/70", border: "border-teal-200", surface: "bg-white/75" },
};

interface AdminMetricCardProps {
  title: string;
  value: ReactNode;
  badge?: string;
  description?: string;
  icon?: string;
  tone?: AdminStatusTone;
  variant?: "plain" | "filled";
  className?: string;
}

const AdminMetricCard = ({
  title,
  value,
  badge,
  description,
  icon,
  tone = "slate",
  variant = "plain",
  className,
}: AdminMetricCardProps) => {
  const toneClass = adminStatusToneClass[tone];
  const metricTone = metricToneClass[tone];
  const isFilled = variant === "filled";

  return (
    <div
      className={cn(
        "rounded-lg border shadow-sm p-4 flex flex-col",
        isFilled ? metricTone.cardBg : "bg-white",
        isFilled ? metricTone.border : "border-slate-200",
        isFilled ? "hover:shadow-md transition-shadow" : undefined,
        className,
      )}
    >
      <div className="flex items-center justify-between mb-1.5 gap-2">
        <span className="text-xs text-slate-500 truncate">{title}</span>
        {badge && (
          <span
            className={cn(
              "inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold shrink-0",
              isFilled ? metricTone.surface : toneClass.bg,
              toneClass.text,
            )}
          >
            {badge}
          </span>
        )}
      </div>

      <div className="flex items-end justify-between flex-1 gap-3">
        <span className={cn("text-2xl font-bold", toneClass.text)}>{value}</span>
        {icon && (
          <div
            className={cn(
              "flex items-center justify-center w-8 h-8 rounded-lg border shrink-0",
              isFilled ? metricTone.surface : toneClass.bg,
              toneClass.border,
            )}
          >
            <i className={cn(icon, toneClass.text)} />
          </div>
        )}
      </div>

      {description && <p className="text-xs text-slate-400 mt-1.5">{description}</p>}
    </div>
  );
};

export default AdminMetricCard;
